import { Clock, Star, User, Calendar, Globe, Users, Timer } from "lucide-react";
import { useSelector } from "react-redux";
import { COLORS } from "../../constants/colors";

// Utility functions extracted for better organization
const getDifficultyColor = (difficulty) => {
  const colors = {
    beginner: "bg-green-100 text-green-800 border-green-200",
    intermediate: "bg-yellow-100 text-yellow-800 border-yellow-200",
    advanced: "bg-red-100 text-red-800 border-red-200",
  };
  return colors[difficulty?.toLowerCase()] || colors.intermediate;
};

const formatPrice = (price, isFree) => {
  if (price === 0 || isFree) return "Free";
  return `$${price}`;
};

const getButtonText = (seatsAvailable, isRegistered) => {
  if (isRegistered) return "Registered";
  if (seatsAvailable === 0) return "Fully Booked";
  return "Register";
};

const getDescription = (course) => {
  const { description, courseDescription } = course;
  const validDescription = description || courseDescription;
  
  if (validDescription?.trim() && validDescription !== 'z') {
    return validDescription;
  }
  
  return "Master essential skills and advance your career with this comprehensive course designed for professionals.";
};

// Course detail row component for better composition
const CourseDetailRow = ({ icon: Icon, label, value, className = "" }) => (
  <div className={`flex items-center justify-between ${className}`}>
    <div className="flex items-center text-gray-700">
      <Icon className="w-4 h-4 mr-2" style={{ color: COLORS.purple }} />
      <span className="text-sm font-medium">{label}</span>
    </div>
    <span className="text-sm text-gray-600">{value}</span>
  </div>
);

const CourseCard = ({ course, onClick }) => {
  const userId = useSelector((state) => state.auth.id);
  const isFull = course.seatsAvailable === 0;
  const isRegistered = course.registeredUsers?.some(user => user._id === userId);
  const isDisabled = isFull || isRegistered;

  const handleButtonClick = () => {
    if (!isDisabled && onClick) {
      onClick(course);
    }
  };

  return (
    <div className="bg-white border-2 border-gray-300 rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col h-full group">
      
      <div className="p-6 flex flex-col flex-1">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-bold text-gray-900 pr-4 leading-tight transition-colors group-hover:opacity-80"
              style={{ color: 'inherit' }}
              onMouseEnter={(e) => e.target.style.color = COLORS.purple}
              onMouseLeave={(e) => e.target.style.color = 'inherit'}>
            {course.title || course.name || course.courseName}
          </h3>
          <span className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-semibold flex-shrink-0 border border-gray-200">
            {formatPrice(course.price, course.isFree)}
          </span>
        </div>

        {/* Description */}
        <p className="text-gray-600 text-sm mb-5 line-clamp-2 leading-relaxed">
          {getDescription(course)}
        </p>

        {/* Course Details */}
        <div className="space-y-4 mb-5">
          <CourseDetailRow
            icon={Timer}
            label={`${course.duration || 8} Week${course.duration !== 1 ? 's' : ''}`}
            value={
              <div className="flex items-center">
                <Star className="w-4 h-4 mr-2 text-yellow-500 fill-current" />
                <span className="text-sm font-medium">{course.rating || "4.8"}</span>
              </div>
            }
          />
          
          <CourseDetailRow
            icon={Clock}
            label={course.time || "04:10 AM"}
            value={
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" style={{ color: COLORS.purple }} />
                <span className="text-sm text-gray-600">{course.date || "July 8, 2025"}</span>
              </div>
            }
          />
          
          <CourseDetailRow
            icon={User}
            label="Instructor"
            value={
              <span className="text-sm text-gray-600">{course.instructor || "test2"}</span>
            }
          />
          
          <CourseDetailRow
            icon={Globe}
            label="Timezone"
            value={
              <span className="text-sm text-gray-600">{course.timezone || "America/Panama"}</span>
            }
          />
          
          <CourseDetailRow
            icon={Users}
            label="Seats"
            value={
              <span className={`text-sm font-medium ${isFull ? 'text-red-500' : ''}`} style={!isFull ? { color: COLORS.purple } : {}}>
                {isFull ? 'Full' : `${course.seatsAvailable || 10} available`}
              </span>
            }
          />
        </div>

        {/* Difficulty Badge */}
        <div className="mb-5">
          <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${getDifficultyColor(course.difficulty || course.difficultyLevel)}`}>
            {course.difficultyLevel ? course.difficultyLevel.charAt(0).toUpperCase() + course.difficultyLevel.slice(1) : course.difficulty || "Intermediate"}
          </span>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Action Button */}
        <button 
          onClick={handleButtonClick}
          disabled={isDisabled}
          className={`w-full font-semibold py-2 px-4 rounded-lg transition-all duration-200 text-sm mt-auto ${
            isDisabled 
              ? 'bg-gray-400 text-white cursor-not-allowed shadow-none' 
              : 'text-white hover:opacity-90 hover:shadow-lg active:transform active:scale-95 shadow-md'
          }`}
          style={!isDisabled ? { backgroundColor: COLORS.purple } : {}}
        >
          {getButtonText(course.seatsAvailable, isRegistered)}
        </button>
      </div>
    </div>
  );
};

export default CourseCard; 